import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import Modal from './Modal';
import { SCALE_MIN, SCALE_MAX } from '../data/surveyQuestions';

const SCALE = Array.from({ length: SCALE_MAX - SCALE_MIN + 1 }, (_, i) => SCALE_MIN + i);

/**
 * Post-case feedback, shown ~30s after the final diagnosis is revealed, late
 * enough that the reveal and its explanation have landed, early enough that
 * people are still in the room.
 */
export default function SurveyModal({ questions, caseTitle, onSubmit, onSkip }) {
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const filled = questions.filter((q) => {
    const v = answers[q.id];
    return q.type === 'text' ? (v || '').trim().length > 0 : v !== undefined;
  }).length;

  const send = () => {
    setSending(true);
    const cleaned = {};
    questions.forEach((q) => {
      const v = answers[q.id];
      if (q.type === 'text') {
        if ((v || '').trim()) cleaned[q.id] = v.trim().slice(0, 1000);
      } else if (v !== undefined) {
        cleaned[q.id] = v;
      }
    });
    onSubmit(cleaned);
  };

  return (
    <Modal title="How did that case go?" onClose={onSkip} wide>
      <div className="flex items-start gap-2.5 mb-5">
        <div className="w-9 h-9 rounded-full bg-accent-tint flex items-center justify-center flex-shrink-0">
          <MessageSquare className="w-4 h-4 text-accent" />
        </div>
        <p className="text-body text-ink-2">
          Three quick questions on {caseTitle ? <span className="font-medium text-ink">{caseTitle}</span> : 'this case'}.
          Answers are used to see how the group engaged, they are not marked.
        </p>
      </div>

      <div className="space-y-5">
        {questions.map((q, i) => (
          <div key={q.id}>
            <p className="text-body font-medium text-ink mb-2">
              <span className="text-accent mr-1.5">{i + 1}.</span>{q.prompt}
            </p>

            {q.type === 'scale' && (
              <>
                <div className="flex gap-1.5">
                  {SCALE.map((n) => (
                    <button
                      key={n}
                      onClick={() => setAnswer(q.id, n)}
                      aria-pressed={answers[q.id] === n}
                      aria-label={`${q.prompt}, ${n} of ${SCALE_MAX}`}
                      className={`flex-1 py-2.5 rounded-ctl border text-body font-semibold transition ${
                        answers[q.id] === n
                          ? 'bg-accent border-accent text-white'
                          : 'border-line text-ink-2 hover:border-accent/40 hover:bg-accent-tint'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                {(q.low || q.high) && (
                  <div className="flex justify-between mt-1">
                    <span className="text-meta text-ink-2">{q.low}</span>
                    <span className="text-meta text-ink-2">{q.high}</span>
                  </div>
                )}
              </>
            )}

            {q.type === 'choice' && (
              <div className="space-y-1.5">
                {(q.options || []).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setAnswer(q.id, opt)}
                    aria-pressed={answers[q.id] === opt}
                    className={`w-full text-left px-3 py-2 rounded-ctl border text-body transition ${
                      answers[q.id] === opt
                        ? 'bg-accent-tint border-accent text-accent'
                        : 'border-line text-ink hover:border-accent/40'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {q.type === 'text' && (
              <textarea
                rows={3}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                placeholder="A sentence is plenty…"
                aria-label={q.prompt}
                className="w-full px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25 resize-y"
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t border-line">
        <button onClick={onSkip} className="px-3 py-2 text-meta text-ink-2 hover:bg-accent-tint rounded-ctl transition">
          Skip this time
        </button>
        <button
          onClick={send}
          disabled={!filled || sending}
          className="bg-accent text-white px-5 py-2.5 rounded-ctl text-body font-medium hover:bg-accent-strong transition disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Submit feedback'}
        </button>
      </div>
    </Modal>
  );
}
